"use client";

import React from "react";
import { usePathname } from "next/navigation";
import {
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  UserCheck,
  Clock,
} from "lucide-react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { cn } from "@/app/_module/lib/utils";
import { routes } from "@/app/_module/constants/routes";
import { useImportProgress, ImportJob } from "./ImportProgressContext";

interface ImportProgressWidgetProps {
  hasActiveCall?: boolean;
  hasActiveSms?: boolean;
}

// Individual job card component to properly use hooks
interface ImportJobCardProps {
  job: ImportJob;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onDismiss: () => void;
}

const ImportJobCard: React.FC<ImportJobCardProps> = ({
  job,
  isExpanded,
  onToggleExpanded,
  onDismiss,
}) => {
  // Simulated progress state
  const [simulatedProgress, setSimulatedProgress] = React.useState(0);

  // Simulate progress animation when processing and backend reports 0
  React.useEffect(() => {
    if (job.status === "processing" && job.processedLeads === 0) {
      const totalLeads = job.totalLeads || 100;
      // Faster for smaller imports, slower for larger ones
      // 150 leads should take about 7-8 seconds to reach 85%
      const estimatedDuration = Math.max(5000, totalLeads * 50);
      const targetProgress = 85;
      const incrementInterval = 100;
      const incrementAmount = targetProgress / (estimatedDuration / incrementInterval);

      const interval = setInterval(() => {
        setSimulatedProgress((prev) => {
          const next = prev + incrementAmount;
          if (next >= targetProgress) {
            return targetProgress;
          }
          return next;
        });
      }, incrementInterval);

      return () => clearInterval(interval);
    }

    // When completing or assigning, animate to 100%
    if (job.status === "completing" || job.status === "assigning") {
      setSimulatedProgress(100);
    }

    // Reset when completed, failed, or rate-limited (cooldown)
    if (job.status === "completed" || job.status === "failed" || job.status === "cooldown") {
      setSimulatedProgress(100);
    }
  }, [job.status, job.processedLeads, job.totalLeads]);

  // Calculate display values
  const getProgressValues = () => {
    // If backend is reporting real progress, use it
    if (job.processedLeads > 0) {
      return {
        progress: (job.processedLeads / job.totalLeads) * 100,
        displayCount: job.processedLeads,
      };
    }

    // For completing/completed/assigning, show 100%
    if (job.status === "completing" || job.status === "completed" || job.status === "assigning") {
      return {
        progress: 100,
        displayCount: job.totalLeads,
      };
    }

    // Use simulated progress during processing
    return {
      progress: simulatedProgress,
      displayCount: Math.floor((simulatedProgress / 100) * job.totalLeads),
    };
  };

  const { progress, displayCount } = getProgressValues();

  // What happened to every row: new, already in your leads, the same person twice
  // in this file, or failed. People already in your leads are not errors; they get
  // their own list.
  const created = job.stats?.leadsCreated ?? 0;
  const existing = job.stats?.alreadyInLeads ?? 0;
  const repeats = job.stats?.repeatsInFile ?? 0;
  const failedRows = job.stats?.errors ?? 0;
  const totalRows = job.stats?.totalRows ?? created + failedRows;
  const failures = (job.errors || []).filter((e) => e.kind !== "duplicate");
  const duplicateRows = (job.errors || []).filter((e) => e.kind === "duplicate");
  const assigned = job.assignmentStats;
  const cleanDuplicateMessage = (message: string) =>
    message.replace(/^Skipped:\s*/i, "").replace(/\s*\((ID|slot)[: ][^)]+\)/g, "");
  // Rows that didn't import, grouped by WHY: "13 not imported (no phone number)" instead of
  // "13 failed" and five copies of "Skipped: no phone number — a lead needs a phone to be called."
  const personName = (lead: any) =>
    lead?.name || [lead?.firstName ?? lead?.firstname, lead?.lastName ?? lead?.lastname].filter(Boolean).join(" ");
  const failureGroups = Object.entries(
    failures.reduce<Record<string, { count: number; names: string[] }>>((acc, f) => {
      const reason = cleanDuplicateMessage(f.error || "").split(" — ")[0].replace(/\.$/, "").trim() || "Unknown error";
      const group = (acc[reason] ??= { count: 0, names: [] });
      group.count += 1;
      const name = personName(f.lead);
      if (name) group.names.push(name);
      return acc;
    }, {})
  ).sort((a, b) => b[1].count - a[1].count);
  const oneReason = failureGroups.length === 1 && failureGroups[0][1].count === failedRows ? failureGroups[0][0] : null;

  return (
    <Card
      className={cn(
        "w-96 shadow-2xl border-2 transition-all duration-300",
        job.status === "processing" && "border-blue-500 bg-blue-50",
        job.status === "completing" && "border-green-500 bg-green-50",
        job.status === "assigning" && "border-purple-500 bg-purple-50",
        job.status === "completed" && "border-green-500 bg-green-50",
        job.status === "failed" && "border-red-500 bg-red-50",
        job.status === "cooldown" && "border-amber-500 bg-amber-50"
      )}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-1">
            {job.status === "processing" && (
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0" />
            )}
            {job.status === "completing" && (
              <Loader2 className="h-5 w-5 text-green-600 animate-spin flex-shrink-0" />
            )}
            {job.status === "assigning" && (
              <UserCheck className="h-5 w-5 text-purple-600 animate-pulse flex-shrink-0" />
            )}
            {job.status === "completed" && (
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            )}
            {job.status === "failed" && (
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            )}
            {job.status === "cooldown" && (
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0 overflow-hidden">
              <div
                className="font-semibold text-sm truncate max-w-[250px]"
                title={job.fileName}
              >
                {job.fileName.length > 35
                  ? `${job.fileName.substring(0, 32)}...`
                  : job.fileName}
              </div>
              <div className="text-xs text-gray-600 truncate">
                {job.status === "processing" &&
                  `${displayCount} / ${job.totalLeads} leads`}
                {job.status === "completing" &&
                  `${job.totalLeads} / ${job.totalLeads} leads`}
                {job.status === "assigning" && "Assigning leads..."}
                {job.status === "completed" &&
                  (existing > 0
                    ? `${created.toLocaleString()} new · ${existing.toLocaleString()} already in your leads`
                    : `${created.toLocaleString()} new leads`)}
                {job.status === "failed" && "Import failed"}
                {job.status === "cooldown" && "Rate limited — cooling down"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {(job.status === "completed" || job.status === "failed" || job.status === "cooldown") && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={onToggleExpanded}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={onDismiss}
              disabled={job.status === "processing" || job.status === "completing" || job.status === "assigning"}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Progress Bar (for processing, completing, and assigning states) */}
        {(job.status === "processing" || job.status === "completing" || job.status === "assigning") && (
          <div className="mb-3">
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-300 ease-out",
                  job.status === "processing" && "bg-blue-600",
                  job.status === "completing" && "bg-green-600",
                  job.status === "assigning" && "bg-purple-600"
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {job.status === "processing"
                ? `Processing: ${displayCount}/${job.totalLeads} records (${Math.round(progress)}%)`
                : job.message}
            </div>
          </div>
        )}

        {/* Status Badge */}
        <div className="flex items-center gap-2 mb-2">
          {job.status === "processing" && (
            <Badge
              variant="outline"
              className="text-xs bg-blue-100 text-blue-800 border-blue-300"
            >
              Importing...
            </Badge>
          )}
          {job.status === "completing" && (
            <Badge
              variant="outline"
              className="text-xs bg-green-100 text-green-800 border-green-300"
            >
              Completing...
            </Badge>
          )}
          {job.status === "assigning" && (
            <Badge
              variant="outline"
              className="text-xs bg-purple-100 text-purple-800 border-purple-300"
            >
              Assigning to Agents...
            </Badge>
          )}
          {job.status === "completed" && (
            <Badge
              variant="outline"
              className="text-xs bg-green-100 text-green-800 border-green-300"
            >
              Complete
            </Badge>
          )}
          {job.status === "failed" && (
            <Badge
              variant="outline"
              className="text-xs bg-red-100 text-red-800 border-red-300"
            >
              Failed
            </Badge>
          )}
          {job.status === "cooldown" && (
            <Badge
              variant="outline"
              className="text-xs bg-amber-100 text-amber-800 border-amber-300"
            >
              Cooling down
            </Badge>
          )}
          <span className="text-xs text-gray-500">
            {new Date(job.startTime).toLocaleTimeString()}
          </span>
        </div>

        {/* WHO GETS THEM — visible collapsed or not. A missed "Assign to" pick used to finish as a
            plain "Complete" and the leads read as vanished (Moe Ray, 2026-10-13). */}
        {job.assignTo !== undefined && job.status !== "failed" && (
          <div
            className={cn(
              "text-xs mb-2",
              job.assignTo ? "text-purple-700" : "text-amber-800"
            )}
          >
            {job.status !== "completed" ? (
              job.assignTo ? (
                <>
                  {job.status === "assigning" ? "Assigning to " : "Will assign to "}
                  <span className="font-semibold">{job.assignTo}</span>
                </>
              ) : (
                "Nobody picked in “Assign to” — these will stay unassigned"
              )
            ) : job.assignTo && assigned && (assigned.successful > 0 || (assigned.alreadyTheirs ?? 0) > 0) ? (
              <>Assigned to <span className="font-semibold">{job.assignTo}</span></>
            ) : (
              <>
                {job.assignTo ? `Not assigned to ${job.assignTo}` : "Not assigned to anyone"} ·{" "}
                <a href={routes["lead-assignment"].path} className="font-semibold underline">
                  Assign them
                </a>
              </>
            )}
          </div>
        )}

        {/* Expanded Details */}
        {isExpanded && job.status === "completed" && job.stats && (
          <div className="mt-3 pt-3 border-t space-y-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white p-2 rounded">
                <div className="text-gray-600">New leads</div>
                <div className="font-bold text-green-600">
                  {created.toLocaleString()}
                </div>
              </div>
              <div className="bg-white p-2 rounded">
                <div className="text-gray-600">Already in your leads</div>
                <div className="font-bold text-gray-900">
                  {existing.toLocaleString()}
                </div>
              </div>
              <div className="bg-white p-2 rounded">
                <div className="text-gray-600">Not imported</div>
                <div
                  className={cn(
                    "font-bold",
                    failedRows > 0 ? "text-red-600" : "text-gray-400"
                  )}
                >
                  {failedRows.toLocaleString()}
                </div>
              </div>
              <div className="bg-white p-2 rounded">
                <div className="text-gray-600">Rows in file</div>
                <div className="font-bold text-blue-600">
                  {totalRows.toLocaleString()}
                </div>
              </div>
            </div>
            {repeats > 0 && (
              <div className="text-xs text-gray-600">
                {repeats.toLocaleString()} more {repeats === 1 ? "row was" : "rows were"} the same person twice in this file, merged into one lead.
              </div>
            )}

            {assigned && (assigned.successful > 0 || assigned.failed > 0 || (assigned.heldByOthers ?? 0) > 0) && (
              <div className="bg-white rounded p-2 text-xs text-gray-700 space-y-0.5">
                <div>
                  <span className="font-semibold text-purple-700">{assigned.successful.toLocaleString()}</span> assigned
                  {(assigned.alreadyTheirs ?? 0) > 0 &&
                    ` · ${assigned.alreadyTheirs!.toLocaleString()} the agent already had`}
                </div>
                {(assigned.heldByOthers ?? 0) > 0 && (
                  <div>
                    {assigned.heldByOthers!.toLocaleString()} kept by other agents (not moved)
                  </div>
                )}
                {assigned.failed > 0 && (
                  <div className="text-red-700">{assigned.failed.toLocaleString()} not assigned</div>
                )}
              </div>
            )}

            {duplicateRows.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-xs font-medium text-amber-900 mb-2">
                  Already in your leads ({duplicateRows.length.toLocaleString()})
                </div>
                <div className="text-xs text-amber-900 max-h-32 overflow-y-auto space-y-1">
                  {duplicateRows.slice(0, 5).map((row, idx) => (
                    <div key={idx} className="bg-white rounded px-2 py-1 border border-amber-100">
                      {cleanDuplicateMessage(row.error)}
                    </div>
                  ))}
                  {duplicateRows.length > 5 && (
                    <div className="text-center pt-1">
                      + {(duplicateRows.length - 5).toLocaleString()} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rows that didn't import, one line per reason */}
            {failures.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-xs font-medium text-red-800 mb-2">
                  Not imported ({failures.length.toLocaleString()})
                </div>
                <div className="text-xs text-red-700 max-h-32 overflow-y-auto space-y-2">
                  {failureGroups.map(([reason, group]) => (
                    <div key={reason} className="bg-white rounded p-2 border border-red-100">
                      <div className="font-medium text-red-800">
                        {reason.charAt(0).toUpperCase() + reason.slice(1)}: {group.count.toLocaleString()}
                      </div>
                      {group.names.length > 0 && (
                        <div className="text-gray-600 mt-1">
                          {group.names.slice(0, 5).join(", ")}
                          {group.count > 5 && ` + ${(group.count - 5).toLocaleString()} more`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-xs text-gray-500">
              Duration:{" "}
              {job.endTime
                ? Math.round(
                    (new Date(job.endTime).getTime() -
                      new Date(job.startTime).getTime()) /
                      1000
                  )
                : 0}
              s
            </div>
          </div>
        )}

        {/* Failed Details */}
        {isExpanded && job.status === "failed" && (
          <div className="mt-3 pt-3 border-t">
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <div className="text-xs text-red-800">{job.message}</div>
            </div>
          </div>
        )}

        {/* Cooldown (rate-limit) Details */}
        {isExpanded && job.status === "cooldown" && (
          <div className="mt-3 pt-3 border-t">
            <div className="bg-amber-50 border border-amber-200 rounded p-2">
              <div className="text-xs text-amber-800">{job.message}</div>
            </div>
          </div>
        )}

        {/* Collapsed cooldown hint */}
        {!isExpanded && job.status === "cooldown" && (
          <div className="text-xs text-amber-700">
            Too many uploads in a short time — wait a moment and try again.
          </div>
        )}

        {/* Message for completed without expansion */}
        {!isExpanded && job.status === "completed" && (
          <div className="text-xs text-gray-600">
            {created.toLocaleString()} new
            {existing > 0 && <span className="ml-1">• {existing.toLocaleString()} already in your leads</span>}
            {failedRows > 0 && (
              <span className="text-red-600 ml-1">
                • {failedRows.toLocaleString()} not imported{oneReason ? ` (${oneReason})` : ""}
              </span>
            )}
            {assigned && assigned.successful > 0 && (
              <span className="text-purple-600 ml-1">
                • {assigned.successful.toLocaleString()} assigned
              </span>
            )}
            {assigned && (assigned.heldByOthers ?? 0) > 0 && (
              <span className="text-amber-700 ml-1">
                • {assigned.heldByOthers!.toLocaleString()} kept by other agents
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ImportProgressWidget: React.FC<ImportProgressWidgetProps> = ({
  hasActiveCall = false,
  hasActiveSms = false,
}) => {
  const { activeImports, dismissImport } = useImportProgress();
  const [expandedJobs, setExpandedJobs] = React.useState<Set<string>>(
    new Set()
  );
  const pathname = usePathname();
  const previousPathnameRef = React.useRef(pathname);

  // Clear completed/failed imports when navigating to a different page
  React.useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      // Route changed - dismiss all completed and failed imports
      activeImports.forEach((job) => {
        if (
          job.status === "completed" ||
          job.status === "failed" ||
          job.status === "cooldown"
        ) {
          dismissImport(job.id);
        }
      });
      previousPathnameRef.current = pathname;
    }
  }, [pathname, activeImports, dismissImport]);

  if (activeImports.length === 0) return null;

  const toggleExpanded = (jobId: string) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  // Calculate position based on active widgets
  const getBottomPosition = () => {
    let bottom = 24; // Base bottom spacing
    // Native shell: clear the glass dock + home indicator (the desktop
    // offsets below assume no bottom tab bar).
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/m")) {
      bottom += 96;
    }
    if (hasActiveCall) {
      bottom += 100; // Offset for the active-call widget
    } else if (hasActiveSms) {
      bottom += 100; // Offset for the SMS widget (if no call)
    } else {
      // Clear the always-present collapsed dialer launcher button (fixed bottom-4,
      // ~56px tall) so the import widget stacks ABOVE it instead of landing on top.
      bottom += 64;
    }
    return bottom;
  };

  return (
    <div
      className="fixed right-[calc(1.5rem+env(safe-area-inset-right,0px))] z-[60] flex flex-col gap-3"
      style={{ bottom: `${getBottomPosition()}px` }}
    >
      {activeImports.map((job) => (
        <ImportJobCard
          key={job.id}
          job={job}
          isExpanded={expandedJobs.has(job.id)}
          onToggleExpanded={() => toggleExpanded(job.id)}
          onDismiss={() => dismissImport(job.id)}
        />
      ))}
    </div>
  );
};
